import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn, formatTime } from '@/lib/utils';
import { Navigation, Moon, Package, Container } from 'lucide-react';
import { t } from '@/i18n';
import { getCarrier } from '@/data/arrivalsData';
import { stateStyles, stateLabel } from './arrivalsUi';
import { RiskBadge } from './RiskBadge';
import { noShowRisk } from '@/lib/noShowRisk';

// Left border = no-show risk, the same meaning as in the Akce queue (#117).
const riskBorder = { high: 'border-l-red-500', medium: 'border-l-amber-400' };

// One loading in today's list. With a risk badge the state is plain text, so a card has one loud label.
export const LoadingCard = ({ loading: l, onSelect }) => {
  const carrier = getCarrier(l.carrierId);
  const TypeIcon = l.type === 'silo' ? Container : Package;
  const tomorrow = l.eta && new Date(l.eta).getDate() !== new Date().getDate();
  const risk = noShowRisk(l);
  const showRisk = risk && risk.level !== 'low';

  return (
    <Card
      onClick={() => onSelect(l)}
      className={cn(
        'p-3 cursor-pointer hover:shadow-md transition-all border-l-4',
        (showRisk && riskBorder[risk.level]) || 'border-l-transparent',
        l.state === 'loaded' && 'opacity-70',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="text-center shrink-0 w-12">
          <div className="text-sm font-bold tabular-nums">{formatTime(l.plannedAt)}</div>
          <div className="text-[10px] text-muted-foreground">{t('arrivals.plan')}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className="font-semibold text-sm">{l.plate}</span>
            {l.storyId && <span className="text-[10px] text-muted-foreground">{l.storyId}</span>}
            {showRisk
              ? <span className="text-xs text-muted-foreground">{stateLabel(l.state)}</span>
              : <Badge variant="outline" className={cn('text-[11px]', stateStyles[l.state])}>{stateLabel(l.state)}</Badge>}
            {showRisk && <RiskBadge risk={risk} />}
            {l.escalated && <Badge variant="outline" className="text-[11px] border-red-300 text-red-700">{t('arrivals.toPlanner')}</Badge>}
          </div>
          <div className="text-xs text-muted-foreground sm:truncate">
            <span>{carrier.name}</span>
            <span className="hidden sm:inline"> · </span>
            <span className="block sm:inline">{l.driver.name}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1"><TypeIcon className="h-3 w-3" />{l.material}</span>
            <span>{l.gate}</span>
            {l.gpsKm != null && l.state === 'en-route' && (
              <span className="flex items-center gap-1 text-sky-700"><Navigation className="h-3 w-3" />{l.gpsKm} km</span>
            )}
            {l.power && (
              <span className="flex items-center gap-1 text-orange-700"><Moon className="h-3 w-3" />{t('arrivals.powerFrom')} {l.power}</span>
            )}
          </div>
        </div>
        {l.eta && (
          <div className="text-right shrink-0">
            <div className="text-sm font-semibold tabular-nums">{formatTime(l.eta)}</div>
            <div className="text-[10px] text-muted-foreground">
              {tomorrow ? t('arrivals.tomorrow') : l.state === 'loaded' ? t('arrivals.loadedAt') : t('arrivals.eta')}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
